import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import styles from "./index.module.css";
import fieldStyles from "./components/SettingsField/SettingsField.module.css";
import PanelHeader from "BSM/components/PanelHeader";
import Loader from "shared/Loader";
import AlertNotice from "shared/AlertNotice";
import EditableSection from "./components/EditableSection";
import SettingsField from "./components/SettingsField";
import TabBar from "BSM/components/TabBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  settings as settingsApi,
  products as productsApi,
  errorMessage,
} from "shared/api";

const SETTINGS_TABS = [{ key: "notifications", label: "Notifications" }];

const recipientShape = PropTypes.shape({
  name: PropTypes.string,
  department: PropTypes.string,
  email: PropTypes.string,
});

const EMPTY_RECIPIENT = { name: "", department: "", email: "" };

// A notification section used to hold exactly one recipient, and older settings
// documents still store it as a bare object. Normalize both shapes to a list so
// the editor always has at least one row to render.
const toRecipientList = (value) => {
  const rows = Array.isArray(value) ? value : value ? [value] : [];
  return rows.length ? rows : [{ ...EMPTY_RECIPIENT }];
};

function RecipientsBody({ editing, draft, setDraft }) {
  const rows = toRecipientList(draft);

  const updateRow = (idx, key) => (value) =>
    setDraft(
      rows.map((row, i) => (i === idx ? { ...row, [key]: value } : row))
    );
  const addRow = () => setDraft([...rows, { ...EMPTY_RECIPIENT }]);
  const removeRow = (idx) => setDraft(rows.filter((_, i) => i !== idx));

  return (
    <div className={styles.recipients}>
      {rows.map((row, idx) => (
        <div className={styles.recipientRow} key={`recipient-${idx}`}>
          <div className={styles.recipientGrid}>
            <SettingsField
              label="Name"
              value={row.name}
              editing={editing}
              onChange={updateRow(idx, "name")}
            />
            <SettingsField
              label="Department"
              value={row.department}
              editing={editing}
              onChange={updateRow(idx, "department")}
            />
            <SettingsField
              label="Email Address"
              type="email"
              value={row.email}
              editing={editing}
              onChange={updateRow(idx, "email")}
            />
          </div>
          {/* The last remaining contact stays put — a section with nobody in it
              would silently notify no one. */}
          {editing && rows.length > 1 ? (
            <button
              type="button"
              className={styles.removeBtn}
              aria-label="Remove contact"
              onClick={() => removeRow(idx)}
              title="Remove this contact."
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          ) : (
            <span />
          )}
        </div>
      ))}

      {editing && (
        <button
          type="button"
          className={styles.addBtn}
          onClick={addRow}
          title="Add another contact."
        >
          + Add Contact
        </button>
      )}
    </div>
  );
}

RecipientsBody.propTypes = {
  editing: PropTypes.bool.isRequired,
  draft: PropTypes.oneOfType([
    recipientShape,
    PropTypes.arrayOf(recipientShape),
  ]),
  setDraft: PropTypes.func.isRequired,
};

function DefaultBody({ editing, draft, setDraft }) {
  return (
    <div className={styles.singleField}>
      <SettingsField
        label="Weeks Remaining"
        type="number"
        min={1}
        max={52}
        value={draft}
        editing={editing}
        onChange={setDraft}
      />
    </div>
  );
}

DefaultBody.propTypes = {
  editing: PropTypes.bool.isRequired,
  draft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setDraft: PropTypes.func.isRequired,
};

function SkuField({ value, editing, onChange }) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const timerRef = useRef(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // A row can be removed (unmounting this field) while a debounced fetch is
  // still pending or in flight. Clear the timer and bump the request id so a
  // late resolution is ignored — otherwise we setState on an unmounted field.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      reqIdRef.current += 1;
    },
    []
  );

  const fetchSuggestions = (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    productsApi
      .list({ search: query, pageSize: 8 })
      .then((res) => {
        if (reqId !== reqIdRef.current) return;
        const items = res?.items || [];
        const skus = [
          ...new Set(items.map((p) => p.sku || p.netsuiteSku).filter(Boolean)),
        ];
        setSuggestions(skus);
        setOpen(skus.length > 0);
        setLoading(false);
      })
      .catch(() => {
        if (reqId === reqIdRef.current) {
          setSuggestions([]);
          setOpen(false);
          setLoading(false);
        }
      });
  };

  const handleChange = (e) => {
    const q = e.target.value;
    onChange(q);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchSuggestions(q), 200);
  };

  const selectSku = (sku) => {
    onChange(sku);
    setOpen(false);
    setSuggestions([]);
  };

  if (!editing) {
    return <span className={fieldStyles.input}>{value || "—"}</span>;
  }

  return (
    <div className={styles.skuField} ref={wrapperRef}>
      <input
        type="text"
        className={`${fieldStyles.input} ${fieldStyles.editing}`}
        value={value}
        onChange={handleChange}
        onFocus={() => {
          if (value && value.trim().length >= 2) fetchSuggestions(value);
        }}
        placeholder="Search SKU..."
        aria-label="SKU"
      />
      {loading && <span className={styles.skuLoading}>…</span>}
      {open && suggestions.length > 0 && (
        <ul className={styles.skuDropdown}>
          {suggestions.map((sku) => (
            <li key={sku}>
              <button
                type="button"
                className={styles.skuOption}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectSku(sku);
                }}
                title={`Select SKU ${sku}.`}
              >
                {sku}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

SkuField.propTypes = {
  value: PropTypes.string,
  editing: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

function ItemSpecificBody({ editing, draft, setDraft }) {
  const rows = draft || [];

  const updateRow = (idx, key) => (value) =>
    setDraft(
      rows.map((row, i) => (i === idx ? { ...row, [key]: value } : row))
    );
  const addRow = () => setDraft([...rows, { sku: "", weeksRemaining: "" }]);
  const removeRow = (idx) => setDraft(rows.filter((_, i) => i !== idx));

  return (
    <div className={styles.itemSpecific}>
      <div className={styles.itemHeaderRow}>
        <span className={styles.itemLabel}>SKU</span>
        <span className={styles.itemLabel}>Weeks Remaining</span>
        <span />
      </div>

      {rows.map((row, idx) => (
        <div className={styles.itemRow} key={`item-${idx}`}>
          <SkuField
            value={row.sku}
            editing={editing}
            onChange={updateRow(idx, "sku")}
          />
          <SettingsField
            type="number"
            min={0}
            value={row.weeksRemaining}
            editing={editing}
            onChange={updateRow(idx, "weeksRemaining")}
            ariaLabel="Weeks Remaining"
          />
          {editing ? (
            <button
              type="button"
              className={styles.removeBtn}
              aria-label="Remove item"
              onClick={() => removeRow(idx)}
              title="Remove this item."
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          ) : (
            <span />
          )}
        </div>
      ))}

      {editing && (
        <button
          type="button"
          className={styles.addBtn}
          onClick={addRow}
          title="Add another item row."
        >
          + Add Item
        </button>
      )}
    </div>
  );
}

ItemSpecificBody.propTypes = {
  editing: PropTypes.bool.isRequired,
  draft: PropTypes.arrayOf(
    PropTypes.shape({
      sku: PropTypes.string,
      weeksRemaining: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  setDraft: PropTypes.func.isRequired,
};

// Item-specific weeks map to a schema Number (default 1). A blank or invalid
// entry would otherwise persist as null (Mongoose casts "" -> null), leaving the
// override with no usable threshold. Coerce to the schema default so the saved
// row is always a valid, predictable number.
const toNumber = (value) => {
  const num = Number(value);
  if (
    value === "" ||
    value === null ||
    value === undefined ||
    Number.isNaN(num)
  )
    return 1;
  return num < 0 ? 0 : num;
};

// The default-weeks threshold is 1–52 in the settings schema. Say so rather
// than quietly saving a different number than the one typed.
const validWeeks = (value) => {
  const num = Number(value);
  if (value === "" || !Number.isInteger(num) || num < 1 || num > 52) {
    return Promise.reject(
      new Error("Weeks Remaining must be a whole number from 1 to 52.")
    );
  }
  return Promise.resolve(num);
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Recipient emails drive who receives Out-of-Stock / Re-Order alerts, so a
// malformed address would silently drop notifications. Reject a non-empty
// invalid email; EditableSection surfaces the thrown message and keeps the
// section open with the draft intact. Rows left completely blank are dropped
// rather than persisted as empty contacts.
const persistRecipientsDraft = (persist, key) => (draft) => {
  const rows = toRecipientList(draft).map((row) => ({
    name: (row?.name || "").trim(),
    department: (row?.department || "").trim(),
    email: (row?.email || "").trim(),
  }));

  for (const row of rows) {
    if ((row.email || row.department) && !row.name) {
      return Promise.reject(
        new Error(`Please enter a name for "${row.email || row.department}".`)
      );
    }
    if (row.email && !EMAIL_RE.test(row.email)) {
      return Promise.reject(
        new Error(
          `Please enter a valid email address for "${row.name || row.email}".`
        )
      );
    }
  }

  const filled = rows.filter((row) => row.name || row.department || row.email);
  return persist({ [key]: filled.length ? filled : [{ ...EMPTY_RECIPIENT }] });
};

const normalizeItems = (rows = []) =>
  rows
    .filter((row) => String(row?.sku ?? "").trim() !== "")
    .map((row) => ({
      ...row,
      sku: String(row.sku).trim(),
      weeksRemaining: toNumber(row.weeksRemaining),
    }));

// SKUs come from NetSuite, so an Item Specific SKU must be one the catalog
// already has: a typo would otherwise save as a rule that never fires. Each
// SKU may appear once, and its weeks must be a whole number of 0 or more.
// Resolves to the rows with each SKU in the catalog's own spelling.
const validateItems = async (rows = []) => {
  const items = rows.filter((row) => String(row?.sku ?? "").trim() !== "");
  const seen = new Set();
  for (const row of items) {
    const sku = String(row.sku).trim();
    if (seen.has(sku.toUpperCase())) {
      throw new Error(`${sku} is listed twice. Keep one row per SKU.`);
    }
    seen.add(sku.toUpperCase());
    const weeks = Number(row.weeksRemaining);
    if (row.weeksRemaining === "" || !Number.isInteger(weeks) || weeks < 0) {
      throw new Error(
        `Weeks Remaining for ${sku} must be a whole number of 0 or more.`
      );
    }
  }
  const checked = await Promise.all(
    items.map(async (row) => {
      const sku = String(row.sku).trim();
      const res = await productsApi.list({
        search: `sku:${sku}`,
        pageSize: 20,
      });
      const match = (res?.items || [])
        .flatMap((p) => [p.sku, p.netsuiteSku])
        .find((s) => s && String(s).toUpperCase() === sku.toUpperCase());
      if (!match) {
        throw new Error(
          `${sku} isn't a SKU in the catalog. Pick one from the list.`
        );
      }
      return { ...row, sku: String(match) };
    })
  );
  return normalizeItems(checked);
};

export default function Settings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    settingsApi
      .getSettings()
      .then((res) => {
        if (reqId !== reqIdRef.current) return;
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (reqId !== reqIdRef.current) return;
        setError(errorMessage(err, "Couldn't load the store settings."));
        setLoading(false);
      });
  }, []);

  // Sections with unsaved edits. Leaving the page (in-app or by closing the
  // tab) asks first, as the product editor does.
  const history = useHistory();
  const [dirtySections, setDirtySections] = useState(() => new Set());
  const dirtyHandlers = useRef({});
  const onDirty = useCallback((key) => {
    if (!dirtyHandlers.current[key]) {
      dirtyHandlers.current[key] = (dirty) =>
        setDirtySections((prev) => {
          if (prev.has(key) === dirty) return prev;
          const next = new Set(prev);
          if (dirty) next.add(key);
          else next.delete(key);
          return next;
        });
    }
    return dirtyHandlers.current[key];
  }, []);
  const hasUnsaved = dirtySections.size > 0;
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const unblock = history.block(
      "You have unsaved changes in Settings. Leave without saving?"
    );
    const onUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      unblock();
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [hasUnsaved, history]);

  const persist = (partial) =>
    settingsApi.updateSettings(partial).then((res) => {
      setData(res);
      return res;
    });

  return (
    <div className={styles.container}>
      <PanelHeader title="WWG Store - Settings" />

      <TabBar tabs={SETTINGS_TABS} activeKey="notifications" />

      <div className={styles.content}>
        {error ? (
          <AlertNotice title="Couldn't load settings">{error}</AlertNotice>
        ) : loading || !data ? (
          <Loader size={60} />
        ) : (
          <div className={styles.cards}>
            <div className={styles.card}>
              {/* BugHerd #72: Out-of-Stock alerts fire when a SKU hits zero, so
                  the weeks-remaining thresholds (Default / Item Specific) don't
                  apply here — they belong to Re-Order only. */}
              <EditableSection
                title="Out of Stock"
                onDirtyChange={onDirty("outOfStock")}
                value={data.outOfStock}
                onSave={persistRecipientsDraft(persist, "outOfStock")}
              >
                {(api) => <RecipientsBody {...api} />}
              </EditableSection>
            </div>

            <div className={styles.card}>
              <EditableSection
                title="Re-Order"
                onDirtyChange={onDirty("reorder")}
                value={data.reorder}
                onSave={persistRecipientsDraft(persist, "reorder")}
              >
                {(api) => <RecipientsBody {...api} />}
              </EditableSection>

              <div className={styles.tealDivider} />

              <EditableSection
                title="Default"
                onDirtyChange={onDirty("default")}
                value={data.reorderDefaultWeeks}
                onSave={(draft) =>
                  validWeeks(draft).then((weeks) =>
                    persist({ reorderDefaultWeeks: weeks })
                  )
                }
              >
                {(api) => <DefaultBody {...api} />}
              </EditableSection>

              <EditableSection
                title="Item Specific"
                onDirtyChange={onDirty("items")}
                value={data.reorderItemSpecific}
                onSave={(draft) =>
                  validateItems(draft).then((items) =>
                    persist({ reorderItemSpecific: items })
                  )
                }
              >
                {(api) => <ItemSpecificBody {...api} />}
              </EditableSection>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
